import React, { useEffect, useRef, useState, useMemo, useCallback, memo } from "react"
import { useSearchParams, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import moment from "moment"
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
  Collapse,
  Stack,
  Breadcrumbs,
  Link,
  Avatar,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import SearchIcon from "@mui/icons-material/Search"
import HomeIcon from "@mui/icons-material/Home"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import InventoryIcon from "@mui/icons-material/Inventory"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import ReceiptIcon from "@mui/icons-material/Receipt"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import WarningIcon from "@mui/icons-material/Warning"
import PendingIcon from "@mui/icons-material/Pending"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import RefreshIcon from "@mui/icons-material/Refresh"
import SendIcon from "@mui/icons-material/Send"
import AssessmentIcon from "@mui/icons-material/Assessment"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import TaskAltIcon from "@mui/icons-material/TaskAlt"
import CategoryIcon from "@mui/icons-material/Category"
import {
  fetchProductionOrders,
  fetchProductionTasks,
  clearProductionCache,
} from "store/production/actions"
import {
  selectOrders,
  selectTasks,
  selectLoading,
  selectUpdating,
} from "store/production/reducer"
import { getProducts, patchOrder } from "helpers/api_helper"
import NeededProductsTable from "./NeededProductsTable"
import RequiredMaterialsTable from "./RequiredMaterialsTable"
import OrderDetailsModal from "./OrderDetailsModal"
import SendForPackagingModal from "./sendtoPackagingModal"
import ShippingList from "./shipping-list"

const formatDate = (d) => (d ? moment(d).format("YYYY-MM-DD") : "")

const DATE_FORMATS = [
  "YYYY-MM-DD HH:mm:ss",
  "YYYY-MM-DDTHH:mm:ssZ",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DD",
  "DD/MM/YYYY",
  "MM/DD/YYYY",
]

const toMillis = (v) => {
  if (!v) return 0
  const m = moment(v, DATE_FORMATS, true)
  if (m.isValid()) return m.valueOf()
  const m2 = moment(new Date(v))
  return m2.isValid() ? m2.valueOf() : 0
}

const FILTER_TABS = [
  { value: "DEFAULT", label: "Pending/Stock", icon: <PendingIcon /> },
  { value: "TO-PACKAGE", label: "To Package", icon: <InventoryIcon /> },
  { value: "PACKAGED", label: "Packaged", icon: <CheckCircleIcon /> },
  { value: "INVOICED", label: "Invoiced", icon: <ReceiptIcon /> },
]

const getStatusColor = (status) => {
  switch (status) {
    case "STOCK-OK":
      return { bg: "#dcfce7", color: "#16a34a", label: "Stock OK" }
    case "PENDING-STOCK":
      return { bg: "#fef3c7", color: "#d97706", label: "Pending Stock" }
    case "TO-PACKAGE":
      return { bg: "#dbeafe", color: "#3b82f6", label: "To Package" }
    case "PACKAGED":
      return { bg: "#f3e8ff", color: "#8b5cf6", label: "Packaged" }
    case "INVOICED":
      return { bg: "#e0e7ff", color: "#6366f1", label: "Invoiced" }
    default:
      return { bg: "#f3f4f6", color: "#6b7280", label: status }
  }
}

// Memoized Status Chip
const StatusChip = memo(({ status, t }) => {
  const statusInfo = getStatusColor(status)
  return (
    <Chip
      label={t(statusInfo.label)}
      size="small"
      sx={{
        bgcolor: statusInfo.bg,
        color: statusInfo.color,
        fontWeight: 500,
        fontSize: "0.75rem",
      }}
    />
  )
})

// Memoized Task Indicator
const TaskIndicator = memo(({ order, tasksByOrderMap, t }) => {
  if (order.status === "STOCK-OK") {
    return (
      <Tooltip title={t("Stock OK")}>
        <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 18 }} />
      </Tooltip>
    )
  }

  const tasks = tasksByOrderMap.get(order.id) || []
  if (tasks.length === 0) {
    return (
      <Tooltip title={t("No tasks")}>
        <WarningIcon sx={{ color: "#f59e0b", fontSize: 18 }} />
      </Tooltip>
    )
  }
  const open = tasks.some((task) => task.status !== "FINISHED")
  if (open) {
    return (
      <Tooltip title={t("Tasks in progress")}>
        <CircularProgress size={16} sx={{ color: "#6b7280" }} />
      </Tooltip>
    )
  }
  return (
    <Tooltip title={t("All tasks finished")}>
      <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 18 }} />
    </Tooltip>
  )
})

// Memoized Analytics Panel
const AnalyticsPanel = memo(({ analytics, expandedPanels, setExpandedPanels, t }) => (
  <Card
    elevation={0}
    sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}
  >
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: expandedPanels.analytics ? "1px solid #f3f4f6" : "none",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "pointer",
        bgcolor: "#f8fafc",
      }}
      onClick={() =>
        setExpandedPanels((prev) => ({
          ...prev,
          analytics: !prev.analytics,
        }))
      }
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar
          sx={{ bgcolor: "#3b82f6", color: "#fff", width: 40, height: 40 }}
        >
          <AssessmentIcon />
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color="#111827">
            {t("Production Analytics")}
          </Typography>
          <Typography variant="caption" color="#6b7280">
            {t("Stock status and production readiness overview")}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {analytics.overdueOrders > 0 && (
          <Chip
            icon={<ErrorOutlineIcon sx={{ fontSize: 16 }} />}
            label={`${analytics.overdueOrders} ${t("Overdue")}`}
            size="small"
            sx={{ bgcolor: "#fef2f2", color: "#dc2626", fontWeight: 500 }}
          />
        )}
        <IconButton size="small">
          {expandedPanels.analytics ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Stack>
    </Box>
    <Collapse in={expandedPanels.analytics}>
      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Fulfillment Rate */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: analytics.fulfillmentRate >= 70 ? "#dcfce7" : "#fef3c7",
                    color: analytics.fulfillmentRate >= 70 ? "#16a34a" : "#d97706",
                    width: 48, height: 48,
                  }}
                >
                  <TrendingUpIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" fontWeight={700} color="#111827">
                    {analytics.fulfillmentRate}%
                  </Typography>
                  <Typography variant="caption" color="#6b7280">
                    {t("Ready to Pack")}
                  </Typography>
                </Box>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={analytics.fulfillmentRate}
                sx={{
                  mt: 2, height: 6, borderRadius: 3, bgcolor: "#f3f4f6",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: analytics.fulfillmentRate >= 70 ? "#16a34a" : "#d97706",
                    borderRadius: 3,
                  },
                }}
              />
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                <Typography variant="caption" color="#16a34a" fontWeight={500}>
                  {analytics.stockOkCount} {t("Stock OK")}
                </Typography>
                <Typography variant="caption" color="#d97706" fontWeight={500}>
                  {analytics.pendingStockCount} {t("Pending")}
                </Typography>
              </Stack>
            </Paper>
          </Grid>

          {/* Timeline */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}
            >
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6", width: 48, height: 48 }}>
                  <AccessTimeIcon />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={600} color="#111827">
                  {t("Delivery Timeline")}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("Due Today")}</Typography>
                  <Chip
                    label={analytics.ordersDueToday}
                    size="small"
                    sx={{
                      bgcolor: analytics.ordersDueToday > 0 ? "#fef3c7" : "#f3f4f6",
                      color: analytics.ordersDueToday > 0 ? "#d97706" : "#6b7280",
                      fontWeight: 600, minWidth: 32,
                    }}
                  />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("This Week")}</Typography>
                  <Chip label={analytics.ordersDueThisWeek} size="small" sx={{ bgcolor: "#dbeafe", color: "#3b82f6", fontWeight: 600, minWidth: 32 }} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("Overdue")}</Typography>
                  <Chip
                    label={analytics.overdueOrders}
                    size="small"
                    sx={{
                      bgcolor: analytics.overdueOrders > 0 ? "#fef2f2" : "#f3f4f6",
                      color: analytics.overdueOrders > 0 ? "#dc2626" : "#6b7280",
                      fontWeight: 600, minWidth: 32,
                    }}
                  />
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* Stock Status */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}
            >
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "#f3e8ff", color: "#8b5cf6", width: 48, height: 48 }}>
                  <CategoryIcon />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={600} color="#111827">
                  {t("Products Status")}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("In Stock")}</Typography>
                  <Chip label={analytics.productsInStock} size="small" sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 600, minWidth: 32 }} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("Shortages")}</Typography>
                  <Chip
                    label={analytics.productsWithShortage}
                    size="small"
                    sx={{
                      bgcolor: analytics.productsWithShortage > 0 ? "#fef2f2" : "#f3f4f6",
                      color: analytics.productsWithShortage > 0 ? "#dc2626" : "#6b7280",
                      fontWeight: 600, minWidth: 32,
                    }}
                  />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="#6b7280">{t("Total Needed")}</Typography>
                  <Typography variant="body2" fontWeight={600} color="#111827">{analytics.totalProductsNeeded}</Typography>
                </Stack>
              </Stack>
            </Paper>
          </Grid>

          {/* Tasks Progress */}
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={0}
              sx={{ p: 2.5, borderRadius: 2, border: "1px solid #e5e7eb", height: "100%" }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                  sx={{
                    bgcolor: analytics.taskCompletionRate >= 80 ? "#dcfce7" : "#fef3c7",
                    color: analytics.taskCompletionRate >= 80 ? "#16a34a" : "#d97706",
                    width: 48, height: 48,
                  }}
                >
                  <TaskAltIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h4" fontWeight={700} color="#111827">
                    {analytics.taskCompletionRate}%
                  </Typography>
                  <Typography variant="caption" color="#6b7280">
                    {t("Tasks Complete")}
                  </Typography>
                </Box>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={analytics.taskCompletionRate}
                sx={{
                  mt: 2, height: 6, borderRadius: 3, bgcolor: "#f3f4f6",
                  "& .MuiLinearProgress-bar": {
                    bgcolor: analytics.taskCompletionRate >= 80 ? "#16a34a" : "#d97706",
                    borderRadius: 3,
                  },
                }}
              />
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                <Typography variant="caption" color="#16a34a" fontWeight={500}>
                  {analytics.completedTasks} {t("Done")}
                </Typography>
                <Typography variant="caption" color="#d97706" fontWeight={500}>
                  {analytics.inProgressTasks} {t("In Progress")}
                </Typography>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Critical Products */}
        {analytics.criticalProducts.length > 0 && (
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="#dc2626"
              sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
            >
              <ErrorOutlineIcon fontSize="small" />
              {t("Critical Stock Shortages")}
            </Typography>
            <Grid container spacing={1}>
              {analytics.criticalProducts.map((product) => (
                <Grid item xs={12} sm={6} md={4} lg={2.4} key={product.id}>
                  <Paper
                    elevation={0}
                    sx={{ p: 1.5, borderRadius: 2, border: "1px solid #fecaca", bgcolor: "#fef2f2" }}
                  >
                    <Typography variant="body2" fontWeight={500} color="#111827" noWrap title={product.name}>
                      {product.name}
                    </Typography>
                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="#6b7280">
                        {t("Need")}: {product.quantity}
                      </Typography>
                      <Typography variant="caption" color="#dc2626" fontWeight={600}>
                        {t("Short")}: {Math.abs(product.available)}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </CardContent>
    </Collapse>
  </Card>
))

const ProductionOverview = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  // Redux state - single source of truth for orders and tasks
  const allOrders = useSelector(selectOrders)
  const allTasks = useSelector(selectTasks)
  const reduxLoading = useSelector(selectLoading)
  const reduxUpdating = useSelector(selectUpdating)

  document.title = t("Operations") + " | FlexFlow ERP"

  const urlFilter = searchParams.get("filter") || "DEFAULT"

  const [selectedOrder, setSelectedOrder] = useState(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [productTotals, setProductTotals] = useState([])
  const [modalProductStocks, setModalProductStocks] = useState([])
  const [orderSearch, setOrderSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [packagingModalOpen, setPackagingModalOpen] = useState(false)
  const [orderFilter, setOrderFilter] = useState(urlFilter)
  const [selectedOrders, setSelectedOrders] = useState([])
  const [showShipping, setShowShipping] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [expandedPanels, setExpandedPanels] = useState({
    analytics: true,
    products: true,
    materials: true,
  })

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(orderSearch), 300)
    return () => clearTimeout(timer)
  }, [orderSearch])

  // Sync filter from URL
  useEffect(() => {
    const newFilter = searchParams.get("filter") || "DEFAULT"
    setOrderFilter(newFilter)
  }, [searchParams])

  const productStockCacheRef = useRef(new Map())

  const getStock = useCallback(async (productId) => {
    const cache = productStockCacheRef.current
    if (cache.has(productId)) return cache.get(productId)
    const stock = (await getProducts(productId))[0]?.stock ?? 0
    cache.set(productId, stock)
    return stock
  }, [])

  const clearStockCache = useCallback(() => productStockCacheRef.current.clear(), [])

  // Memoized tasks grouped by order from Redux
  const tasksByOrderMap = useMemo(() => {
    const byOrder = new Map()
    for (const task of allTasks) {
      if (!byOrder.has(task.orderref)) byOrder.set(task.orderref, [])
      byOrder.get(task.orderref).push(task)
    }
    return byOrder
  }, [allTasks])

  const processOrders = useCallback(async (orders) => {
    try {
      const updates = []
      await Promise.all(
        orders
          .filter((o) => ["PENDING-STOCK", "STOCK-OK"].includes(o.status))
          .map(async (order) => {
            const products = JSON.parse(order.products || "[]")
            const diffs = await Promise.all(
              products.map(async (p) => {
                const st = await getStock(p.id)
                return st - parseFloat(p.quantity)
              })
            )
            const stockOk = diffs.every((d) => d >= 0)
            const tasks = tasksByOrderMap.get(order.id) || []
            const allFinished = tasks.length === 0 || tasks.every((task) => task.status === "FINISHED")

            if (order.status === "PENDING-STOCK" && stockOk && allFinished) {
              updates.push({ id: order.id, status: "STOCK-OK" })
            } else if (order.status === "STOCK-OK" && !stockOk) {
              updates.push({ id: order.id, status: "PENDING-STOCK" })
            }
          })
      )
      if (updates.length > 0) {
        await patchOrder({ resource: updates })
        // Refresh orders after status updates
        dispatch(fetchProductionOrders(true))
      }
    } catch (err) {
      console.error("Error processing orders:", err)
    }
  }, [getStock, tasksByOrderMap, dispatch])

  const calculateProductTotals = useCallback(async (orders) => {
    const productMap = new Map()

    for (const order of orders) {
      try {
        const products = JSON.parse(order.products || "[]")
        for (const prod of products) {
          const q = parseFloat(prod.quantity)
          if (!productMap.has(prod.id)) {
            productMap.set(prod.id, {
              id: prod.id,
              name: prod.name,
              quantity: q,
              requirements: prod.requirements,
              stock: 0,
            })
          } else {
            productMap.get(prod.id).quantity += q
          }
        }
      } catch (err) {
        console.error("Invalid product JSON in order", order.id, err)
      }
    }

    const list = Array.from(productMap.values())
    const withStocks = await Promise.all(
      list.map(async (p) => {
        const stock = await getStock(p.id)
        return { ...p, stock, available: stock - p.quantity }
      })
    )

    setProductTotals(withStocks)
  }, [getStock])

  // Fetch data via Redux actions
  const fetchOrders = useCallback((forceRefresh = true) => {
    clearStockCache()
    if (forceRefresh) {
      dispatch(clearProductionCache())
    }
    dispatch(fetchProductionOrders(forceRefresh))
    if (orderFilter === "DEFAULT") {
      dispatch(fetchProductionTasks(forceRefresh))
    }
    setSelectedOrders([])
  }, [dispatch, clearStockCache, orderFilter])

  // Initial fetch on mount and filter change
  useEffect(() => {
    if (!showShipping) {
      fetchOrders()
    }
  }, [orderFilter, showShipping])

  // Memoized orders filtered by status from Redux
  const statusFilteredOrders = useMemo(() => {
    let filtered = allOrders
    if (orderFilter === "DEFAULT") {
      filtered = allOrders.filter((o) => ["PENDING-STOCK", "STOCK-OK"].includes(o.status))
    } else if (orderFilter === "TO-PACKAGE") {
      filtered = allOrders.filter((o) => o.status === "TO-PACKAGE")
    } else if (orderFilter === "PACKAGED") {
      filtered = allOrders.filter((o) => o.status === "PACKAGED")
    } else if (orderFilter === "INVOICED") {
      filtered = allOrders.filter((o) => o.status === "INVOICED")
    }
    return filtered.sort(
      (a, b) => toMillis(a.expected_delivery_date) - toMillis(b.expected_delivery_date)
    )
  }, [allOrders, orderFilter])

  // Process orders for stock status when in DEFAULT filter and data loaded
  useEffect(() => {
    if (orderFilter === "DEFAULT" && statusFilteredOrders.length > 0 && !reduxLoading && !processing) {
      setProcessing(true)
      processOrders(statusFilteredOrders).finally(() => setProcessing(false))
    }
  }, [statusFilteredOrders, orderFilter, reduxLoading, tasksByOrderMap])

  // Calculate product totals when filtered orders change
  useEffect(() => {
    if ((orderFilter === "DEFAULT" || orderFilter === "TO-PACKAGE") && statusFilteredOrders.length > 0) {
      calculateProductTotals(statusFilteredOrders)
    } else {
      setProductTotals([])
    }
  }, [statusFilteredOrders, orderFilter, calculateProductTotals])

  const openDetails = useCallback(async (order) => {
    setSelectedOrder(order)
    try {
      const products = JSON.parse(order.products || "[]")
      const withStock = await Promise.all(
        products.map(async (prod) => {
          const stock = await getStock(prod.id)
          const diff = stock - parseFloat(prod.quantity)
          return { ...prod, stock, diff }
        })
      )
      setModalProductStocks(withStock)
      setDetailsOpen(true)
    } catch (err) {
      console.error("Error parsing order products", err)
      setDetailsOpen(true)
    }
  }, [getStock])

  const closeDetails = useCallback(() => {
    setDetailsOpen(false)
    setSelectedOrder(null)
    setModalProductStocks([])
  }, [])

  const bulkSendForPackaging = useCallback(async () => {
    try {
      const payload = {
        resource: selectedOrders.map((id) => ({ id, status: "TO-PACKAGE" })),
      }
      await patchOrder(payload)
      setSelectedOrders([])
      // Refresh Redux store
      dispatch(clearProductionCache())
      dispatch(fetchProductionOrders(true))
    } catch (err) {
      console.error("Error sending orders for packaging", err)
    }
  }, [selectedOrders, dispatch])

  const bulkSendToInvoice = useCallback(async () => {
    try {
      const payload = {
        resource: selectedOrders.map((id) => ({ id, status: "READY-TO-INVOICE" })),
      }
      await patchOrder(payload)
      setSelectedOrders([])
      // Refresh Redux store
      dispatch(clearProductionCache())
      dispatch(fetchProductionOrders(true))
    } catch (err) {
      console.error("Error sending orders to invoice", err)
    }
  }, [selectedOrders, dispatch])

  const canSelect = useCallback((order) => {
    if (orderFilter === "DEFAULT") return order.status === "STOCK-OK"
    if (orderFilter === "PACKAGED") return order.status === "PACKAGED"
    return false
  }, [orderFilter])

  const handleTabChange = useCallback((event, newValue) => {
    if (newValue === "DEFAULT") {
      setSearchParams({})
    } else {
      setSearchParams({ filter: newValue })
    }
  }, [setSearchParams])

  const handleCheckboxChange = useCallback((e, orderId) => {
    e.stopPropagation()
    if (e.target.checked) {
      setSelectedOrders((prev) => [...prev, orderId])
    } else {
      setSelectedOrders((prev) => prev.filter((id) => id !== orderId))
    }
  }, [])

  const handleSearchChange = useCallback((e) => {
    setOrderSearch(e.target.value)
  }, [])

  const showTasksUI = orderFilter === "DEFAULT"
  const loading = reduxLoading || processing

  // Memoized filtered orders with search from Redux data
  const filteredOrders = useMemo(() => {
    if (!debouncedSearch) return statusFilteredOrders
    const searchLower = debouncedSearch.toLowerCase()
    return statusFilteredOrders.filter((ord) =>
      Object.values(ord).some((val) =>
        String(val ?? "").toLowerCase().includes(searchLower)
      )
    )
  }, [statusFilteredOrders, debouncedSearch])

  // Compute analytics from Redux data
  const analytics = useMemo(() => {
    const today = moment().startOf("day")
    const endOfWeek = moment().endOf("week")

    const stockOkOrders = statusFilteredOrders.filter((o) => o.status === "STOCK-OK")
    const pendingStockOrders = statusFilteredOrders.filter((o) => o.status === "PENDING-STOCK")

    const ordersDueToday = statusFilteredOrders.filter((o) => {
      const dueDate = moment(o.expected_delivery_date)
      return dueDate.isSame(today, "day")
    })
    const ordersDueThisWeek = statusFilteredOrders.filter((o) => {
      const dueDate = moment(o.expected_delivery_date)
      return dueDate.isBetween(today, endOfWeek, "day", "[]")
    })
    const overdueOrders = statusFilteredOrders.filter((o) => {
      const dueDate = moment(o.expected_delivery_date)
      return dueDate.isBefore(today, "day")
    })

    const productsWithShortage = productTotals.filter((p) => p.available < 0)
    const productsInStock = productTotals.filter((p) => p.available >= 0)
    const totalProductsNeeded = productTotals.reduce((sum, p) => sum + p.quantity, 0)

    let totalTasks = 0, completedTasks = 0, inProgressTasks = 0
    tasksByOrderMap.forEach((tasks) => {
      totalTasks += tasks.length
      completedTasks += tasks.filter((t) => t.status === "FINISHED").length
      inProgressTasks += tasks.filter((t) => t.status !== "FINISHED").length
    })

    const fulfillmentRate = statusFilteredOrders.length > 0
      ? Math.round((stockOkOrders.length / statusFilteredOrders.length) * 100) : 0
    const taskCompletionRate = totalTasks > 0
      ? Math.round((completedTasks / totalTasks) * 100) : 100

    return {
      totalOrders: statusFilteredOrders.length,
      stockOkCount: stockOkOrders.length,
      pendingStockCount: pendingStockOrders.length,
      fulfillmentRate,
      ordersDueToday: ordersDueToday.length,
      ordersDueThisWeek: ordersDueThisWeek.length,
      overdueOrders: overdueOrders.length,
      totalProducts: productTotals.length,
      productsWithShortage: productsWithShortage.length,
      productsInStock: productsInStock.length,
      totalProductsNeeded,
      criticalProducts: productsWithShortage.slice(0, 5),
      totalTasks,
      completedTasks,
      inProgressTasks,
      taskCompletionRate,
    }
  }, [statusFilteredOrders, productTotals, tasksByOrderMap])

  // Memoized columns
  const columns = useMemo(() => [
    {
      field: "select",
      headerName: "",
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <Box
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}
          onClick={(e) => {
            // If clicking on the checkbox itself, let it handle the change
            if (e.target.type === "checkbox" || e.target.closest('input[type="checkbox"]')) {
              return
            }
            // Otherwise, open the modal
            e.stopPropagation()
            openDetails(params.row)
          }}
        >
          <Checkbox
            size="small"
            disabled={!canSelect(params.row)}
            checked={selectedOrders.includes(params.row.id)}
            onChange={(e) => handleCheckboxChange(e, params.row.id)}
            onClick={(e) => e.stopPropagation()}
          />
        </Box>
      ),
    },
    { field: "expected_delivery_date", headerName: t("Delivery Date"), flex: 1, minWidth: 120 },
    {
      field: "created",
      headerName: t("Created"),
      flex: 1,
      minWidth: 110,
      valueGetter: (value, row) => formatDate(row.created || row.created_at),
    },
    { field: "id", headerName: t("Order ID"), width: 90 },
    {
      field: "status",
      headerName: t("Status"),
      width: 140,
      renderCell: (params) => <StatusChip status={params.value} t={t} />,
    },
    ...(showTasksUI
      ? [{
          field: "tasks",
          headerName: t("Tasks"),
          width: 70,
          sortable: false,
          renderCell: (params) => (
            <TaskIndicator order={params.row} tasksByOrderMap={tasksByOrderMap} t={t} />
          ),
        }]
      : []),
    {
      field: "label",
      headerName: t("Label"),
      flex: 1,
      minWidth: 100,
      valueGetter: (value, row) => row.label || row.order_label || row.tag || "",
    },
    { field: "name", headerName: t("Customer"), flex: 1.5, minWidth: 150 },
    { field: "delivery_address", headerName: t("Delivery Address"), flex: 2, minWidth: 200 },
  ], [t, showTasksUI, canSelect, selectedOrders, handleCheckboxChange, tasksByOrderMap, openDetails])

  // DataGrid sx styles memoized
  const dataGridSx = useMemo(() => ({
    border: "none",
    "& .MuiDataGrid-columnHeaders": { bgcolor: "#f9fafb", borderBottom: "1px solid #e5e7eb" },
    "& .MuiDataGrid-columnHeaderTitle": {
      fontWeight: 600, fontSize: "0.75rem", color: "#6b7280", textTransform: "uppercase",
    },
    "& .MuiDataGrid-row": { cursor: "pointer", "&:hover": { bgcolor: "#f9fafb" } },
    "& .MuiDataGrid-cell": { borderBottom: "1px solid #f3f4f6", fontSize: "0.875rem" },
    "& .MuiDataGrid-row.stock-ok": { bgcolor: "#f0fdf4" },
  }), [])

  if (showShipping) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", py: 3 }}>
        <Container maxWidth={false}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
            <Link
              component="button"
              underline="hover"
              sx={{ display: "flex", alignItems: "center", color: "#6b7280" }}
              onClick={() => navigate("/customers")}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
              {t("Home")}
            </Link>
            <Link component="button" underline="hover" color="#6b7280" onClick={() => setShowShipping(false)}>
              {t("Operations")}
            </Link>
            <Typography color="#111827" fontWeight={500}>{t("Shipping")}</Typography>
          </Breadcrumbs>
          <Button
            variant="outlined"
            startIcon={<NavigateNextIcon sx={{ transform: "rotate(180deg)" }} />}
            onClick={() => setShowShipping(false)}
            sx={{ mb: 3 }}
          >
            {t("Back to Orders")}
          </Button>
          <ShippingList embedded />
        </Container>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      {/* Header */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e5e7eb", px: 3, py: 2 }}>
        <Container maxWidth={false} disableGutters>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
              <Link
                component="button"
                underline="hover"
                sx={{ display: "flex", alignItems: "center", color: "#6b7280" }}
                onClick={() => navigate("/customers")}
              >
                <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                {t("Home")}
              </Link>
              <Typography color="#111827" fontWeight={500}>{t("Operations")}</Typography>
            </Breadcrumbs>
            <Stack direction="row" spacing={1}>
              <IconButton onClick={fetchOrders} disabled={loading}>
                <RefreshIcon />
              </IconButton>
              {orderFilter === "DEFAULT" && selectedOrders.length > 0 && (
                <Button variant="contained" color="success" startIcon={<SendIcon />} onClick={bulkSendForPackaging}>
                  {t("Send for Packaging")} ({selectedOrders.length})
                </Button>
              )}
              {orderFilter === "PACKAGED" && selectedOrders.length > 0 && (
                <Button variant="contained" color="warning" startIcon={<ReceiptIcon />} onClick={bulkSendToInvoice}>
                  {t("Send to Invoice")} ({selectedOrders.length})
                </Button>
              )}
              {orderFilter === "INVOICED" && (
                <Button
                  variant="contained"
                  startIcon={<LocalShippingIcon />}
                  onClick={() => setShowShipping(true)}
                  sx={{ bgcolor: "#3b82f6" }}
                >
                  {t("Go to Shipping")}
                </Button>
              )}
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* Tabs */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e5e7eb" }}>
        <Container maxWidth={false} disableGutters>
          <Tabs
            value={orderFilter}
            onChange={handleTabChange}
            sx={{
              px: 3,
              "& .MuiTab-root": { textTransform: "none", fontWeight: 500, fontSize: "0.875rem", minHeight: 48 },
              "& .Mui-selected": { color: "#3b82f6" },
              "& .MuiTabs-indicator": { bgcolor: "#3b82f6" },
            }}
          >
            {FILTER_TABS.map((tab) => (
              <Tab key={tab.value} value={tab.value} label={t(tab.label)} icon={tab.icon} iconPosition="start" />
            ))}
          </Tabs>
        </Container>
      </Box>

      {/* Main Content */}
      <Container maxWidth={false} sx={{ py: 3 }}>
        {orderFilter === "DEFAULT" && (
          <AnalyticsPanel
            analytics={analytics}
            expandedPanels={expandedPanels}
            setExpandedPanels={setExpandedPanels}
            t={t}
          />
        )}

        {/* Search */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <TextField
            placeholder={t("Search orders...")}
            value={orderSearch}
            onChange={handleSearchChange}
            size="small"
            sx={{ width: 300, bgcolor: "#fff", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />
          <Chip label={`${filteredOrders.length} ${t("Orders")}`} sx={{ bgcolor: "#e0e7ff", color: "#4338ca", fontWeight: 500 }} />
          {selectedOrders.length > 0 && (
            <Chip label={`${selectedOrders.length} ${t("Selected")}`} sx={{ bgcolor: "#dcfce7", color: "#16a34a", fontWeight: 500 }} />
          )}
        </Stack>

        {/* Orders Table */}
        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
          <DataGrid
            rows={filteredOrders}
            columns={columns}
            loading={loading}
            autoHeight
            disableRowSelectionOnClick
            onRowClick={(params) => openDetails(params.row)}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            sx={dataGridSx}
            getRowClassName={(params) => (params.row.status === "STOCK-OK" ? "stock-ok" : "")}
          />
        </Card>

        {/* Bottom Panels */}
        {(orderFilter === "DEFAULT" || orderFilter === "TO-PACKAGE") && (
          <Stack spacing={3}>
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb" }}>
              <Box
                sx={{
                  px: 3, py: 2,
                  borderBottom: expandedPanels.products ? "1px solid #f3f4f6" : "none",
                  display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer",
                }}
                onClick={() => setExpandedPanels((prev) => ({ ...prev, products: !prev.products }))}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6", width: 36, height: 36 }}>
                    <InventoryIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#111827">{t("Needed Products")}</Typography>
                    <Typography variant="caption" color="#6b7280">{productTotals.length} {t("Products")}</Typography>
                  </Box>
                </Stack>
                <IconButton size="small">
                  {expandedPanels.products ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={expandedPanels.products}>
                <CardContent>
                  <NeededProductsTable products={productTotals} />
                </CardContent>
              </Collapse>
            </Card>

            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb" }}>
              <Box
                sx={{
                  px: 3, py: 2,
                  borderBottom: expandedPanels.materials ? "1px solid #f3f4f6" : "none",
                  display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer",
                }}
                onClick={() => setExpandedPanels((prev) => ({ ...prev, materials: !prev.materials }))}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ bgcolor: "#f3e8ff", color: "#8b5cf6", width: 36, height: 36 }}>
                    <InventoryIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600} color="#111827">{t("Materials Overview")}</Typography>
                    <Typography variant="caption" color="#6b7280">{t("Required materials for production")}</Typography>
                  </Box>
                </Stack>
                <IconButton size="small">
                  {expandedPanels.materials ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={expandedPanels.materials}>
                <CardContent>
                  <RequiredMaterialsTable neededProducts={productTotals} />
                </CardContent>
              </Collapse>
            </Card>
          </Stack>
        )}
      </Container>

      {/* Modals */}
      <SendForPackagingModal
        isOpen={packagingModalOpen}
        toggle={() => setPackagingModalOpen(false)}
        order={selectedOrder}
        onStatusChanged={() => fetchOrders(true)}
      />
      <OrderDetailsModal
        isOpen={detailsOpen}
        toggle={closeDetails}
        order={selectedOrder}
        onRefreshOrders={fetchOrders}
        productsWithStock={modalProductStocks}
      />
    </Box>
  )
}

export default memo(ProductionOverview)
